/**
 * String Furniture Planner Integration
 *
 * Handles postMessage communication with the String Planner iframe and Shopify cart.
 * Documentation: https://planner.stringfurniture.com/docs
 */

class StringFurnitureBYO extends HTMLElement {
  constructor() {
    super();
    this.iframe = null;
    this.sectionId = this.dataset.sectionId;
    this.storeGuid = this.dataset.storeGuid;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.loadTimeout = null;
    this.isLoaded = false;
    
    // Product search cache and collection index
    this.productCache = new Map();
    this.productJsonCache = new Map();
    this.collectionIndex = null;
    this.collectionIndexPromise = null;
    this.cartButtonEnabled = false;
    this.searchTimeout = null;
    
    this.bindMethods();
    this.setupEventListeners();
  }

  connectedCallback() {
    this.missingModal = this.querySelector('modal-dialog.string-byo-missing-modal');

    setTimeout(() => {
      this.loadIframe();
    }, 500);
  }

  bindMethods() {
    this.handleMessage = this.handleMessage.bind(this);
    this.handleRetry = this.handleRetry.bind(this);
    this.handleIframeLoad = this.handleIframeLoad.bind(this);
    this.handleIframeError = this.handleIframeError.bind(this);
  }

  setupEventListeners() {
    window.addEventListener('message', this.handleMessage, false);
    
    const retryButton = this.querySelector('.js-retry-byo');
    if (retryButton) {
      retryButton.addEventListener('click', this.handleRetry);
    }

    // Listen for window errors that might come from the iframe
    window.addEventListener('error', (event) => {
      // Check if error comes from String Furniture iframe
      if (event.filename && event.filename.includes('string')) {
        console.warn('String Furniture BYO: Error caught from iframe:', event.message);
        
        // Specific handling for the length error
        if (event.message && event.message.includes("Cannot read properties of undefined (reading 'length')")) {
          console.warn('String Furniture BYO: Detected timing error, iframe may not be fully loaded');
          // Don't show error to user, this is a timing issue that will resolve
          return;
        }
        
        // For other errors, show our error state after multiple occurrences
        this.retryCount++;
        if (this.retryCount > 2 && !this.isLoaded) {
          this.showError('String Furniture designer encountered an error. Please try refreshing the page.');
        }
      }
    });

    // Listen for unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      if (event.source && event.source.window === this.iframe?.contentWindow) {
        console.warn('String Furniture BYO iframe error:', event.error);
        this.handleIframeError();
      }
    }, true);
  }

  loadIframe() {
    const iframeContainer = this.querySelector('.string-byo-iframe-container');
    const loadingState = this.querySelector('.string-byo-loading');
    const errorState = this.querySelector('.string-byo-error');
    
    if (!iframeContainer || !loadingState) {
      console.error('String Furniture BYO: Required elements not found');
      return;
    }

    // Check Store GUID
    if (!this.storeGuid) {
      console.error('String Furniture BYO: Store GUID required');
      this.showError('Store GUID required. Please configure in section settings.');
      return;
    }
    
    console.log('String Furniture BYO: Loading iframe with Store GUID:', this.storeGuid);

    // Show the loading overlay, but keep the iframe container laid out (not display:none).
    // The embedded planner reads its own window.innerWidth on boot; if the iframe has no
    // layout width it assumes a mobile viewport and opens the "Open a design" dialog.
    loadingState.classList.remove('hidden');
    errorState?.classList.add('hidden');
    iframeContainer.classList.remove('hidden');

    // Create or get iframe
    this.iframe = iframeContainer.querySelector('iframe');
    if (!this.iframe) {
      console.error('String Furniture BYO: Iframe not found');
      this.showError();
      return;
    }

    const minWidth = parseInt(this.dataset.minWidth, 10) || 1024;
    const minHeight = parseInt(this.dataset.minHeight, 10) || 730;
    iframeContainer.style.minWidth = `${minWidth}px`;
    this.iframe.style.minWidth = `${minWidth}px`;
    this.iframe.style.minHeight = `${minHeight}px`;

    // Set up iframe event listeners
    this.iframe.addEventListener('load', this.handleIframeLoad);
    this.iframe.addEventListener('error', this.handleIframeError);

    // Set a timeout for loading
    this.loadTimeout = setTimeout(() => {
      if (!this.isLoaded) {
        console.warn('String Furniture BYO: Load timeout exceeded');
        this.handleIframeError();
      }
    }, 20000); // 20 second timeout to accommodate slower loading

    // Trigger iframe src if not already set
    if (!this.iframe.src || this.iframe.src === 'about:blank') {
      const originalSrc = this.iframe.getAttribute('data-src') || this.iframe.src;
      if (originalSrc) {
        this.iframe.src = this.buildIframeSrc(originalSrc);
      }
    }
  }

  buildIframeSrc(baseSrc) {
    try {
      const url = new URL(baseSrc, window.location.origin);
      const urlDesignTemplate = new URLSearchParams(window.location.search).get('designtemplate');

      if (urlDesignTemplate) {
        url.searchParams.set('designtemplate', urlDesignTemplate);
      }

      return url.toString();
    } catch (error) {
      console.warn('String Furniture BYO: Invalid iframe URL', error);
      return baseSrc;
    }
  }

  getIframeTargetOrigin() {
    const src = this.iframe?.src;

    if (!src || src === 'about:blank') {
      return 'https://planner.stringfurniture.com';
    }

    try {
      return new URL(src).origin;
    } catch (error) {
      return 'https://planner.stringfurniture.com';
    }
  }

  handleIframeLoad() {
    console.log('String Furniture BYO: Iframe loaded');
    
    // Clear timeout
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }

    // Wait a bit for the BYO tool to initialize
    setTimeout(() => {
      this.checkBYOReady();
    }, 1000);
  }

  checkBYOReady() {
    if (this.isLoaded) return;

    console.log('String Furniture BYO: Iframe loaded, waiting for byoLoaded event');
    
    // Don't show iframe yet - wait for byoLoaded event from String Furniture
    // This prevents premature resize events that cause errors in their code
    
    // Set a longer timeout in case byoLoaded event never comes
    setTimeout(() => {
      if (!this.isLoaded) {
        console.warn('String Furniture BYO: byoLoaded event not received after 10 seconds, showing iframe anyway');
        this.isLoaded = true;
        this.showIframe();
      }
    }, 10000); // Increased to 10 seconds
  }

  handleIframeError() {
    console.error('String Furniture BYO: Iframe error occurred');
    
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }

    this.showError();
  }

  showIframe() {
    const iframeContainer = this.querySelector('.string-byo-iframe-container');
    const loadingState = this.querySelector('.string-byo-loading');
    const errorState = this.querySelector('.string-byo-error');

    if (iframeContainer && loadingState) {
      loadingState.classList.add('hidden');
      errorState?.classList.add('hidden');
      iframeContainer.classList.remove('hidden');
      
      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('stringbyo:loaded', {
        detail: { sectionId: this.sectionId },
        bubbles: true
      }));

      console.log('String Furniture BYO: Successfully shown iframe');
    }
  }

  showError(customMessage = null) {
    const iframeContainer = this.querySelector('.string-byo-iframe-container');
    const loadingState = this.querySelector('.string-byo-loading');
    const errorState = this.querySelector('.string-byo-error');

    if (loadingState && errorState) {
      loadingState.classList.add('hidden');
      iframeContainer?.classList.add('hidden');
      errorState.classList.remove('hidden');
      
      if (customMessage) {
        const errorText = errorState.querySelector('p');
        if (errorText) {
          errorText.textContent = customMessage;
        }
      }
    }

    // Dispatch error event
    this.dispatchEvent(new CustomEvent('stringbyo:error', {
      detail: { 
        sectionId: this.sectionId,
        retryCount: this.retryCount,
        message: customMessage 
      },
      bubbles: true
    }));
  }

  handleRetry() {
    if (this.retryCount >= this.maxRetries) {
      console.error('String Furniture BYO: Max retries exceeded');
      return;
    }

    this.retryCount++;
    this.isLoaded = false;
    this.cartButtonEnabled = false;
    
    console.log(`String Furniture BYO: Retry attempt ${this.retryCount}`);
    
    // Reset iframe
    if (this.iframe) {
      this.iframe.removeEventListener('load', this.handleIframeLoad);
      this.iframe.removeEventListener('error', this.handleIframeError);
      this.iframe.src = 'about:blank';
      
      setTimeout(() => {
        this.loadIframe();
      }, 1000);
    }
  }

  handleMessage(event) {
    // Security check
    if (!this.isValidOrigin(event.origin)) {
      console.warn('String Furniture BYO: Invalid origin:', event.origin);
      return;
    }

    const data = event.data;
    console.log('String Furniture BYO: Received message:', data);

    // Handle different message types
    switch(data.event || data.type) {
      case 'byoLoaded':
        this.handleByoLoaded(data);
        break;
      case 'enableCart':
        this.handleEnableCart(data);
        break;
      case 'updateDesign':
      case 'designUpdate': // Handle the actual event String Furniture sends
        this.handleDesignUpdate(data);
        break;
      case 'updateCart':
        this.handleUpdateCart(data);
        break;
      case 'cartUpdate': // Handle cart update from String Furniture
        this.handleCartUpdate(data);
        break;
      case 'addToCart':
        this.handleAddToCart(data);
        break;
      default:
        console.log('String Furniture BYO: Unknown Message', data);
        break;
    }
  }

  handleByoLoaded(data) {
    console.log('String Furniture BYO: Tool loaded successfully');
    
    this.showIframe();
    this.isLoaded = true;
    this.cartButtonEnabled = false;

    // Preload string-furniture collection for SKU-based matching
    this.loadCollectionIndex();
    
    this.dispatchEvent(new CustomEvent('stringbyo:loaded', {
      detail: { data }
    }));
  }

  handleEnableCart(data) {
    console.log('String Furniture BYO: enableCart message received from iframe', data);
  }

  setCartButtonEnabled(enabled) {
    if (this.cartButtonEnabled === enabled) return;

    this.cartButtonEnabled = enabled;

    if (enabled) {
      console.log('String Furniture BYO: Enabling cart button — design has items');
      this.sendMessageToIframe({ event: 'enableCart', data: {} });
    }
  }

  handleDesignUpdate(data) {
    console.log('String Furniture BYO: Design updated', data);
    
    this.currentDesign = data.data || data;
    const items = this.currentDesign?.items || [];
    this.setCartButtonEnabled(items.length > 0);
    
    this.dispatchEvent(new CustomEvent('stringbyo:design-updated', {
      detail: { design: this.currentDesign }
    }));
  }

  async handleAddToCart(data) {
    console.log('String Furniture BYO: Add to cart requested', data);
    
    // Use the stored design data if no data provided
    const designData = data.data || this.currentDesign;
    
    if (!designData || !designData.items || designData.items.length === 0) {
      console.error('String Furniture BYO: No items to add to cart');
      this.showNotification('No items to add to cart', 'error');
      return;
    }

    this.showNotification('Processing your String Furniture design...', 'info');

    try {
      const cartItems = [];
      
      // Process each item in the design
      for (const item of designData.items) {
        console.log('Processing String Furniture item:', item);
        
        // Find matching Shopify variant
        const variant = await this.findShopifyVariant(item);
        
        if (variant) {
          cartItems.push({
            id: variant.id,
            quantity: item.quantity || 1,
            properties: {
              '_string_furniture_ean': item.ean,
              '_string_furniture_article': item.article,
              '_string_furniture_design_id': designData.designid,
              '_string_furniture_design_name': designData.name || 'Custom String Design'
            }
          });
          console.log(`Matched EAN ${item.ean} to Shopify variant ${variant.id}`);
        } else {
          console.warn(`Could not find Shopify variant for EAN: ${item.ean}, Article: ${item.article}`);
        }
      }

      if (cartItems.length === 0) {
        throw new Error('No matching products found in Shopify');
      }

      // Add items to Shopify cart
      const result = await this.addToShopifyCart(cartItems);
      
      if (result.success) {
        this.showNotification('String Furniture design added to cart successfully!', 'success');
        
        this.dispatchEvent(new CustomEvent('stringbyo:cart-updated', {
          detail: { 
            items: cartItems, 
            design: designData,
            result: result 
          }
        }));
        
      } else {
        throw new Error(result.message || 'Failed to add items to cart');
      }
      
    } catch (error) {
      console.error('String Furniture BYO: Cart error:', error);
      this.showNotification(`Error adding to cart: ${error.message}`, 'error');
    }
  }

  async handleCartUpdate(data) {
    console.log('String Furniture BYO: Cart update received from String Furniture', data);
    
    const cartData = data.data || data;
    
    if (!cartData || !cartData.items || cartData.items.length === 0) {
      console.error('String Furniture BYO: No items in cart update');
      this.showNotification('No items to add to cart', 'error');
      this.setCartButtonEnabled(false);
      return;
    }

    console.log(`String Furniture BYO: Processing ${cartData.items.length} items from design "${cartData.name}"`);
    this.showNotification('Adding String Furniture design to cart...', 'info');

    try {
      const cartItems = [];
      let successCount = 0;
      let failureCount = 0;
      const missingItems = [];
      
      // Process each item in the design
      for (const item of cartData.items) {
        console.log('Processing String Furniture item:', {
          ean: item.ean,
          article: item.article,
          quantity: item.quantity
        });
        
        // Find matching Shopify variant
        const variant = await this.findShopifyVariant(item);
        
        if (variant) {
          cartItems.push({
            id: variant.id,
            quantity: item.quantity || 1,
            properties: {
              '_string_furniture_ean': item.ean,
              '_string_furniture_article': item.article,
              '_string_furniture_design_id': cartData.designid,
              '_string_furniture_design_name': cartData.name || 'Custom String Design'
            }
          });
          console.log(`✓ Adding to cart: EAN ${item.ean} (${item.article}) → Shopify variant ${variant.id}`);
          successCount++;
        } else {
          missingItems.push(item);
          console.warn(`✗ Skipping unavailable item: EAN ${item.ean}, Article ${item.article}`);
          failureCount++;
        }
      }

      this.showMissingProductsModal(missingItems);

      // Block only when no products match at all
      if (cartItems.length === 0) {
        const missingLabels = missingItems.map((item) => this.getItemDisplayLabel(item));
        console.error(`String Furniture BYO: CART BLOCKED - No products found (0/${cartData.items.length})`);
        throw new Error(
          `No matching products found in your Shopify store. Missing: ${missingLabels.join(', ')}.`
        );
      }

      if (failureCount > 0) {
        const missingLabels = missingItems.map((item) => this.getItemDisplayLabel(item));
        console.warn(
          `String Furniture BYO: Partial add — ${successCount}/${cartData.items.length} items matched. Missing: ${missingLabels.join(', ')}`
        );
      }

      // Add items to Shopify cart
      console.log(`String Furniture BYO: Adding ${cartItems.length} available items to cart`);
      const result = await this.addToShopifyCart(cartItems);
      
      if (result.success) {
        let message;
        let notificationType;
        
        if (failureCount > 0) {
          const missingLabels = missingItems.map((item) => this.getItemDisplayLabel(item));
          message = `Added ${successCount} of ${cartData.items.length} items to cart. Not in store: ${missingLabels.join(', ')}.`;
          notificationType = 'warning';
        } else {
          message = `String Furniture design "${cartData.name}" added to cart successfully!`;
          notificationType = 'success';
          this.closeMissingProductsModal();
        }
        
        this.showNotification(message, notificationType);
        
        this.dispatchEvent(new CustomEvent('stringbyo:cart-updated', {
          detail: { 
            items: cartItems, 
            design: cartData,
            result: result,
            successCount: successCount,
            failureCount: failureCount
          }
        }));
        
      } else {
        throw new Error(result.message || 'Failed to add items to cart');
      }
      
    } catch (error) {
      console.error('String Furniture BYO: Cart update error:', error);
      if (!this.missingModal?.hasAttribute('open')) {
        this.showNotification(`Error adding to cart: ${error.message}`, 'error');
      }
    }
  }

  handleUpdateCart(data) {
    console.log('String Furniture BYO: Update cart');
    this.currentCartData = data.data || data;
  }

  getItemDisplayLabel(item) {
    const article = item.article || item.articleno || item.ean || '';
    const name = item.name || item.description || item.title || '';
    return name ? `${article} — ${name}` : article;
  }

  showMissingProductsModal(missingItems) {
    if (!this.missingModal) {
      this.missingModal = this.querySelector('modal-dialog.string-byo-missing-modal');
    }
    if (!this.missingModal) return;

    if (!missingItems?.length) {
      this.closeMissingProductsModal();
      return;
    }

    const heading = this.missingModal.querySelector('.string-byo-missing-modal__heading');
    const intro = this.missingModal.querySelector('.string-byo-missing-modal__intro');
    const list = this.missingModal.querySelector('.string-byo-missing-modal__list');

    if (heading) {
      heading.textContent = this.dataset.missingHeading || 'The following items are not available in our store:';
    }
    if (intro) {
      intro.textContent = this.dataset.missingIntro || 'These products from your design cannot be added to cart.';
    }
    if (list) {
      list.textContent = '';
      missingItems.forEach((item) => {
        const li = document.createElement('li');
        li.textContent = this.getItemDisplayLabel(item);
        list.appendChild(li);
      });
    }

    if (!this.missingModal.hasAttribute('open')) {
      this.missingModal.open();
    }
  }

  closeMissingProductsModal() {
    if (!this.missingModal) {
      this.missingModal = this.querySelector('modal-dialog.string-byo-missing-modal');
    }
    if (this.missingModal?.hasAttribute('open')) {
      this.missingModal.close();
    }
  }

  buildSuggestUrl(query, limit = 5) {
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': String(limit),
      'resources[options][fields]': 'variants.barcode,variants.sku,title,variants.title,vendor'
    });
    return `/search/suggest.json?${params.toString()}`;
  }

  async fetchProductJson(handle) {
    if (this.productJsonCache.has(handle)) {
      return this.productJsonCache.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      const product = await response.json();
      this.productJsonCache.set(handle, product);
      return product;
    } catch (error) {
      console.warn('String Furniture BYO: Product JSON fetch failed', handle, error);
      return null;
    }
  }

  normalizeVariant(variant, productTitle) {
    return {
      id: variant.id,
      sku: variant.sku,
      barcode: variant.barcode,
      title: variant.title || productTitle,
      available: variant.available
    };
  }

  matchVariantInProduct(product, { ean, sku }) {
    if (!product?.variants?.length) return null;

    if (ean) {
      const byBarcode = product.variants.find((v) => v.barcode === ean);
      if (byBarcode) return this.normalizeVariant(byBarcode, product.title);
    }

    if (sku) {
      const exact = product.variants.find((v) => v.sku === sku);
      if (exact) return this.normalizeVariant(exact, product.title);

      if (sku.startsWith('SF')) {
        const withoutPrefix = sku.slice(2);
        const byPrefix = product.variants.find((v) => v.sku === withoutPrefix);
        if (byPrefix) return this.normalizeVariant(byPrefix, product.title);
      }
    }

    return null;
  }

  async resolveVariantFromSuggestProducts(products, { ean, sku }) {
    for (const product of products) {
      const fullProduct = await this.fetchProductJson(product.handle);
      const source = fullProduct || product;
      const variant = this.matchVariantInProduct(source, { ean, sku });

      if (variant) {
        if (variant.available === false) {
          console.warn('String Furniture BYO: Found match but variant is out of stock', variant);
          continue;
        }
        return variant;
      }
    }

    return null;
  }

  async loadCollectionIndex() {
    if (this.collectionIndex) return this.collectionIndex;
    if (this.collectionIndexPromise) return this.collectionIndexPromise;

    this.collectionIndexPromise = (async () => {
      const index = { bySku: new Map(), byEan: new Map() };
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        try {
          const response = await fetch(`/collections/string-furniture/products.json?limit=250&page=${page}`);
          if (!response.ok) break;

          const data = await response.json();
          const products = data.products || [];

          for (const product of products) {
            for (const variant of product.variants || []) {
              const entry = this.normalizeVariant(variant, product.title);

              if (variant.sku) {
                index.bySku.set(variant.sku, entry);
                if (variant.sku.startsWith('SF')) {
                  index.bySku.set(variant.sku.slice(2), entry);
                }
              }

              if (variant.barcode) {
                index.byEan.set(variant.barcode, entry);
              }
            }
          }

          hasMore = products.length === 250;
          page += 1;
        } catch (error) {
          console.warn('String Furniture BYO: Collection index load failed', error);
          break;
        }
      }

      this.collectionIndex = index;
      console.log(`String Furniture BYO: Indexed ${index.bySku.size} SKUs from string-furniture collection`);
      return index;
    })();

    return this.collectionIndexPromise;
  }

  lookupInCollectionIndex(stringItem) {
    if (!this.collectionIndex) return null;

    const { bySku, byEan } = this.collectionIndex;

    if (stringItem.ean && byEan.has(stringItem.ean)) {
      return byEan.get(stringItem.ean);
    }

    const articleRef = stringItem.article || stringItem.articleno;
    if (!articleRef) return null;

    if (bySku.has(articleRef)) return bySku.get(articleRef);

    if (articleRef.startsWith('SF') && bySku.has(articleRef.slice(2))) {
      return bySku.get(articleRef.slice(2));
    }

    return null;
  }

  // Product matching functions
  async findShopifyVariant(stringItem) {
    console.log('String Furniture BYO: Finding variant for', stringItem);

    if (stringItem.variant_id) {
      console.log('String Furniture BYO: Using provided variant ID', stringItem.variant_id);
      return { id: stringItem.variant_id };
    }

    const cacheKey = `${stringItem.ean || ''}-${stringItem.article || stringItem.articleno || ''}`;
    if (this.productCache.has(cacheKey)) {
      const cached = this.productCache.get(cacheKey);
      if (Date.now() - cached.timestamp < 300000) {
        console.log('String Furniture BYO: Using cached variant result');
        return cached.variant;
      }
      this.productCache.delete(cacheKey);
    }

    let variant = null;

    await this.loadCollectionIndex();
    variant = this.lookupInCollectionIndex(stringItem);
    if (variant) {
      console.log('String Furniture BYO: Found variant in collection index', variant);
      this.productCache.set(cacheKey, { variant, timestamp: Date.now() });
      return variant;
    }

    if (stringItem.ean) {
      variant = await this.searchByEAN(stringItem.ean, stringItem.article || stringItem.articleno);
      if (variant) {
        console.log('String Furniture BYO: Found variant by EAN', variant);
        this.productCache.set(cacheKey, { variant, timestamp: Date.now() });
        return variant;
      }
    }

    const articleRef = stringItem.article || stringItem.articleno;
    if (articleRef) {
      variant = await this.searchBySKU(articleRef, stringItem.ean);
      if (variant) {
        console.log('String Furniture BYO: Found variant by SKU', variant);
        this.productCache.set(cacheKey, { variant, timestamp: Date.now() });
        return variant;
      }
    }

    if (stringItem.name) {
      variant = await this.searchByName(stringItem.name);
      if (variant) {
        console.log('String Furniture BYO: Found variant by name', variant);
        this.productCache.set(cacheKey, { variant, timestamp: Date.now() });
        return variant;
      }
    }

    console.warn('String Furniture BYO: No variant found for', stringItem);
    this.productCache.set(cacheKey, { variant: null, timestamp: Date.now() });
    return null;
  }

  async searchByEAN(ean, sku) {
    try {
      const response = await fetch(this.buildSuggestUrl(ean, 5));
      const data = await response.json();
      const products = data.resources?.results?.products || [];

      if (products.length > 0) {
        return this.resolveVariantFromSuggestProducts(products, { ean, sku });
      }
    } catch (error) {
      console.warn('String Furniture BYO: EAN search failed', error);
    }

    return null;
  }

  async searchBySKU(sku, ean) {
    try {
      const response = await fetch(this.buildSuggestUrl(sku, 5));
      const data = await response.json();
      const products = data.resources?.results?.products || [];

      if (products.length > 0) {
        return this.resolveVariantFromSuggestProducts(products, { ean, sku });
      }
    } catch (error) {
      console.warn('String Furniture BYO: SKU search failed', error);
    }

    return null;
  }

  async searchByName(name) {
    try {
      const searchTerm = name
        .replace(/[^\w\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      const response = await fetch(this.buildSuggestUrl(searchTerm, 5));
      const data = await response.json();
      
      if (data.resources?.results?.products?.length > 0) {
        for (const product of data.resources.results.products) {
          if (product.vendor?.toLowerCase().includes('string') || 
              product.title?.toLowerCase().includes('string') ||
              product.type?.toLowerCase().includes('string')) {
            
            const fullProduct = await this.fetchProductJson(product.handle);
            const source = fullProduct || product;

            if (source.variants?.length > 0) {
              const variant = source.variants.find((v) => v.available);
              if (variant) {
                return this.normalizeVariant(variant, source.title);
              }
              
              console.warn(`String Furniture BYO: Found product "${source.title}" by name but no available variants`);
            }
          }
        }
      }
    } catch (error) {
      console.warn('String Furniture BYO: Name search failed', error);
    }
    return null;
  }

  getCartSections() {
    const sections = ['cart-icon-bubble'];
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer) {
      const wrapper = cartDrawer.closest('.shopify-section');
      if (wrapper) sections.push(wrapper.id.replace('shopify-section-', ''));
    }

    return sections;
  }

  async addToShopifyCart(items) {
    try {
      console.log('String Furniture BYO: Adding items to Shopify cart:', items);

      const routes = window.theme?.routes || {};
      const cartAddUrl = routes.cartAdd || '/cart/add';
      const requestBody = {
        items,
        sections: this.getCartSections().join(','),
        sections_url: window.location.pathname
      };

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('String Furniture BYO: Cart add response:', result);

      await this.updateCartUI(result, items);

      return { success: true, data: result };
    } catch (error) {
      console.error('String Furniture BYO: Add to cart error:', error);
      return { success: false, message: error.message };
    }
  }

  async updateCartUI(cartData, addedItems = []) {
    const themeSettings = window.theme?.settings || {};
    const themeRoutes = window.theme?.routes || {};
    const cartDrawer = document.querySelector('cart-drawer');

    if (themeSettings.afterAtc === 'page') {
      setTimeout(() => {
        window.location.href = themeRoutes.cart || '/cart';
      }, 300);
      return;
    }

    if (cartData.sections?.['cart-icon-bubble']) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = cartData.sections['cart-icon-bubble'];
      }
    }

    const openDrawer = themeSettings.afterAtc === 'drawer';

    if (cartDrawer && cartData.sections) {
      setTimeout(async () => {
        await cartDrawer.renderContents(cartData, openDrawer);
      }, 300);
    } else if (cartDrawer) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      if (openDrawer) {
        setTimeout(() => cartDrawer.open(), 500);
      }
    } else if (window.location.pathname === (themeRoutes.cart || '/cart')) {
      const cartItems = document.querySelector('cart-items');
      if (cartItems?.dataset.empty === 'true') {
        window.location.reload();
      } else if (typeof cartItems?.refresh === 'function') {
        cartItems.refresh();
      }
    }

    await this.notifyCartChanged(addedItems);
  }

  async notifyCartChanged(addedItems = []) {
    const cartUrl = window.theme?.routes?.cart || '/cart';

    try {
      const response = await fetch(`${cartUrl}.js`);
      if (!response.ok) return;

      const cart = await response.json();
      document.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: {
          cart,
          variantId: addedItems[0]?.id
        }
      }));
    } catch (error) {
      console.warn('String Furniture BYO: Failed to notify cart listeners', error);
    }
  }

  showNotification(message, type = 'info') {
    const notification = this.querySelector('.string-byo-notification');
    if (!notification) return;

    const iconMap = {
      loading: '<div class="loading-spinner-small"></div>',
      success: '✓',
      error: '✗',
      info: 'ℹ'
    };

    const colorMap = {
      loading: 'bg-blue-100 border-blue-400 text-blue-700',
      success: 'bg-green-100 border-green-400 text-green-700',
      error: 'bg-red-100 border-red-400 text-red-700',
      info: 'bg-blue-100 border-blue-400 text-blue-700'
    };

    // Update notification content
    notification.innerHTML = `
      <div class="flex">
        <div class="py-1">
          ${iconMap[type]}
        </div>
        <div class="ml-2">
          <p class="font-bold">${message}</p>
        </div>
      </div>
    `;

    // Update classes
    notification.className = `string-byo-notification absolute top-4 right-4 px-4 py-3 rounded ${colorMap[type]}`;
    
    // Show notification
    notification.classList.remove('hidden');

    // Auto-hide after delay (except for loading)
    if (type !== 'loading') {
      setTimeout(() => {
        notification.classList.add('hidden');
      }, type === 'error' ? 8000 : 4000);
    }
  }

  sendMessageToIframe(message) {
    if (this.iframe && this.iframe.contentWindow) {
      try {
        const targetOrigin = this.getIframeTargetOrigin();
        this.iframe.contentWindow.postMessage(message, targetOrigin);
        console.log('String Furniture BYO: Sent message to iframe:', message);
      } catch (error) {
        console.error('String Furniture BYO: Error sending message to iframe:', error);
      }
    }
  }

  isValidOrigin(origin) {
    const allowedOrigins = [
      'https://planner.stringfurniture.com',
      'https://build-your-own.stringfurniture.com',
      'https://www.stringfurniture.com',
      'https://stringfurniture.com'
    ];
    return allowedOrigins.includes(origin);
  }

  disconnectedCallback() {
    // Clean up event listeners
    window.removeEventListener('message', this.handleMessage);
    
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
    }

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    if (this.iframe) {
      this.iframe.removeEventListener('load', this.handleIframeLoad);
      this.iframe.removeEventListener('error', this.handleIframeError);
    }
  }
}

// Register the custom element
customElements.define('string-furniture-byo', StringFurnitureBYO);

// Global error handler for iframe content
window.addEventListener('error', (event) => {
  if (event.filename && event.filename.includes('stringfurniture.com')) {
    console.warn('String Furniture BYO: Caught iframe error', event.error);
    // Don't prevent default - just log it
  }
}, true); 